// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo, useState } from 'react';
import {
  Segment,
  Header,
  Form,
  Button,
  Icon,
  Label,
  Message,
  Modal,
  Divider,
  List,
  Popup,
} from 'semantic-ui-react';
import {
  brokerageIntegrationProviderApi,
  BrokerageIntegrationProvider,
  ConfigureProviderRequest,
  ValidateProviderResponse,
} from '../../api/brokerageIntegrationProviderApi';
import { Brokerage } from '../../api/brokerageApi';
import CapabilityToggleRow from './CapabilityToggleRow';
import RedirectUriHelper from './RedirectUriHelper';
import ConsentScreenCallout from './ConsentScreenCallout';

interface Props {
  brokerageId: string;
  brokerage: Brokerage | null;
  existing: BrokerageIntegrationProvider | null;
  onUpdated: (provider: BrokerageIntegrationProvider) => void;
  onDeleted: () => void;
}

/**
 * Google provider credentials card. Mirrors MicrosoftProviderCard with two
 * Google-specific differences:
 *
 *   1. No tenant_id field — Google has no equivalent (the OAuth client is
 *      created inside a Google Cloud project, not a tenant). The Validate
 *      button enables when client_id + client_secret are filled (no third
 *      required field).
 *
 *   2. The Consent Screen Callout sits above the credentials form to
 *      remind the platform admin to ask brokerage IT to use the "Internal"
 *      OAuth consent screen type — skipping Google's 4–6 week app
 *      verification process for Phase 2's SSO-only scope set.
 *
 * Phase 2 functional toggle is SSO only; Calendar/Email rows render
 * disabled with "Available in Phase 7" tooltips (Google calendar/email
 * lands after Microsoft's Phase 3-6 because the communications-service
 * + ProviderAdapter SPI is introduced there).
 *
 * "Save" is gated on a successful Validate within the same edit session
 * (parallel to the Microsoft card).
 */
function GoogleProviderCard({
  brokerageId,
  brokerage,
  existing,
  onUpdated,
  onDeleted,
}: Props) {
  const [clientId, setClientId] = useState(existing?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [contactEmail, setContactEmail] = useState(existing?.contactEmail ?? '');
  const [contactName, setContactName] = useState(existing?.contactName ?? '');
  const [showSecret, setShowSecret] = useState(false);

  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidateProviderResponse | null>(null);

  const dirty = useMemo(() => {
    return (
      clientId !== (existing?.clientId ?? '') ||
      !!clientSecret ||
      displayName !== (existing?.displayName ?? '') ||
      contactEmail !== (existing?.contactEmail ?? '') ||
      contactName !== (existing?.contactName ?? '')
    );
  }, [clientId, clientSecret, displayName, contactEmail, contactName, existing]);

  // Google: no tenant_id required.
  const canValidate = clientId.trim().length > 0 && clientSecret.trim().length > 0;
  const canSave = validation?.valid === true && dirty;

  const request = (): ConfigureProviderRequest => ({
    clientId: clientId.trim(),
    clientSecret: clientSecret,
    // tenantId intentionally omitted — Google has none
    displayName: displayName.trim() || undefined,
    contactEmail: contactEmail.trim() || undefined,
    contactName: contactName.trim() || undefined,
  });

  const handleValidate = async () => {
    setError(null);
    setValidating(true);
    try {
      const result = await brokerageIntegrationProviderApi.validate(brokerageId, 'GOOGLE', request());
      setValidation(result);
      if (!result.valid) {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const saved = await brokerageIntegrationProviderApi.configure(brokerageId, 'GOOGLE', request());
      onUpdated(saved);
      setClientSecret('');
      setValidation(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      await brokerageIntegrationProviderApi.delete(brokerageId, 'GOOGLE');
      onDeleted();
      setConfirmDelete(false);
      setClientId('');
      setClientSecret('');
      setValidation(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleCapabilityToggle = async (key: keyof Pick<BrokerageIntegrationProvider,
    'ssoEnabled' | 'calendarReadEnabled' | 'calendarWriteEnabled' | 'emailReadEnabled' | 'emailSendEnabled'>,
    nextValue: boolean) => {
    setError(null);
    try {
      const updated = await brokerageIntegrationProviderApi.updateCapabilities(brokerageId, 'GOOGLE', {
        [key]: nextValue,
      });
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Capability update failed');
    }
  };

  const lastValidatedBadge = () => {
    if (!existing) return null;
    if (existing.lastValidationResult === 'NEVER_VALIDATED') {
      return <Label color="grey">Never validated</Label>;
    }
    if (existing.lastValidationResult === 'VALID') {
      return (
        <Popup
          trigger={<Label color="green"><Icon name="check" /> Valid</Label>}
          content={existing.lastValidatedAt ? `Last validated ${existing.lastValidatedAt}` : 'Last validated recently'}
        />
      );
    }
    return (
      <Popup
        trigger={<Label color="red"><Icon name="exclamation" /> {existing.lastValidationResult}</Label>}
        content={existing.lastValidationMessage ?? 'Last validation failed; see logs'}
      />
    );
  };

  return (
    <Segment>
      <Header as="h2">
        <Icon name="google" />
        <Header.Content>
          Google
          <Header.Subheader>
            Per-brokerage Google OAuth client credentials
            {existing && <span style={{ marginLeft: 8 }}>{lastValidatedBadge()}</span>}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <ConsentScreenCallout />

      {brokerage && <RedirectUriHelper brokerage={brokerage} provider="google" />}

      {error && (
        <Message negative>
          <Message.Header>Something went wrong</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {validation?.valid && (
        <Message positive>
          <Message.Header>Validation succeeded</Message.Header>
          <p>{validation.message}</p>
          {validation.grantedScopes.length > 0 && (
            <>
              <p style={{ marginBottom: 4 }}>Granted scopes:</p>
              <List bulleted>
                {validation.grantedScopes.map((scope) => (
                  <List.Item key={scope}>{scope}</List.Item>
                ))}
              </List>
            </>
          )}
        </Message>
      )}

      <Form>
        <Form.Input
          id="google-client-id"
          label={<label htmlFor="google-client-id">Client ID</label>}
          placeholder="OAuth 2.0 Client ID from Google Cloud Console"
          value={clientId}
          onChange={(_, { value }) => {
            setClientId(value);
            setValidation(null);
          }}
        />
        <Form.Field>
          <label htmlFor="google-client-secret">Client Secret</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="google-client-secret"
              type={showSecret ? 'text' : 'password'}
              placeholder={existing?.clientSecretConfigured ? '••••• (configured; paste new to rotate)' : 'New client secret value'}
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                setValidation(null);
              }}
              style={{ flex: 1 }}
            />
            <Button type="button" onClick={() => setShowSecret((s) => !s)} icon>
              <Icon name={showSecret ? 'eye slash' : 'eye'} />
            </Button>
          </div>
          <small style={{ color: '#888' }}>
            Encrypted at rest with envelope encryption. Never logged. Never returned by the API once saved.
          </small>
        </Form.Field>
        <Form.Input
          id="google-display-name"
          label={<label htmlFor="google-display-name">Display name (optional)</label>}
          placeholder='e.g. "Acme Google"'
          value={displayName}
          onChange={(_, { value }) => setDisplayName(value)}
        />
        <Form.Group widths="equal">
          <Form.Input
            id="google-contact-email"
            label={<label htmlFor="google-contact-email">Brokerage IT contact email</label>}
            type="email"
            placeholder="For revalidation-failure notifications"
            value={contactEmail}
            onChange={(_, { value }) => setContactEmail(value)}
          />
          <Form.Input
            label="Brokerage IT contact name"
            value={contactName}
            onChange={(_, { value }) => setContactName(value)}
          />
        </Form.Group>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button primary disabled={!canValidate || validating} loading={validating} onClick={handleValidate}>
            <Icon name="check circle outline" />
            Validate
          </Button>
          <Button color="green" disabled={!canSave || saving} loading={saving} onClick={handleSave}>
            <Icon name="save" />
            Save
          </Button>
          {existing && (
            <Button color="red" basic onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" />
              Delete configuration
            </Button>
          )}
        </div>
      </Form>

      <Divider />

      <Header as="h3">Capabilities</Header>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Toggle on the capabilities the brokerage has authorised in Google
        Cloud Console. Enabling a capability requests the matching OAuth
        scope from Google on the next advisor SSO sign-in; Calendar Write
        and Email Send don't have an advisor-UI surface yet but the scope
        is captured so a later phase can use it without a re-consent.
      </p>

      <CapabilityToggleRow
        label="Single sign-on"
        description="Adds 'Sign in with Google' to the brokerage's advisor login page."
        enabled={existing?.ssoEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('ssoEnabled', v)}
      />
      <CapabilityToggleRow
        label="Calendar — Read"
        description="Advisors' Google Calendar events become visible on their dashboard and client profiles."
        enabled={existing?.calendarReadEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('calendarReadEnabled', v)}
      />
      <CapabilityToggleRow
        label="Calendar — Write"
        description="Advisors can schedule meetings (with Google Meet links) from inside client profiles."
        enabled={existing?.calendarWriteEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('calendarWriteEnabled', v)}
      />
      <CapabilityToggleRow
        label="Email — Read"
        description="Recent Gmail subjects to/from a client surface on the client profile."
        enabled={existing?.emailReadEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('emailReadEnabled', v)}
      />
      <CapabilityToggleRow
        label="Email — Send"
        description="Advisors send emails to clients on behalf of their own Gmail mailbox."
        enabled={existing?.emailSendEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('emailSendEnabled', v)}
      />

      <Modal open={confirmDelete} size="small" onClose={() => setConfirmDelete(false)}>
        <Modal.Header>Delete Google integration?</Modal.Header>
        <Modal.Content>
          <p>This will:</p>
          <List bulleted>
            <List.Item>Remove the credentials from the LOA Platform</List.Item>
            <List.Item>
              Disable the Google Identity Provider on this brokerage's advisor realm
              — existing federated advisor accounts persist
            </List.Item>
            <List.Item>(Phase 7+) revoke all advisor calendar/email connections for this brokerage</List.Item>
          </List>
          <p>
            <b>Confirm only if brokerage IT has rotated the secret or you intend to remove the
            integration entirely.</b>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="red" loading={deleting} onClick={handleDelete}>
            Delete configuration
          </Button>
        </Modal.Actions>
      </Modal>
    </Segment>
  );
}

export default GoogleProviderCard;
